import { CacheHitResponse, CacheKey, GeoService, ListenerManager, OnDataReadyListener, ResponseStatus, SearchRequest, SearchResponse, ServiceCache, ServiceCacheEntry } from "@esignal/polygon-service-api";
import { CacheKey_toString } from "./CacheKey_toString";
import { isUseIDBCache } from "./sww/USE_IDB";
import { putData } from "./indexed-db/put";
import { DB_NAME } from "./indexed-db/constants";
import { getAllKeys, getData } from "./indexed-db/get";
import { deleteData } from "./indexed-db/delete";

const FETCH_TIMEOUT = 10; // 10ms

export const getDefaultValidUntil = (): number => Date.now() + 60 * 60 * 1000; // plus one hour

export class ServiceCacheImpl implements ServiceCache {
	private readonly _cache = new Map<string, ServiceCacheEntry>();
	private readonly listenerManagers = new Map<string, ListenerManager<OnDataReadyListener>>();

	constructor(private readonly geoService: GeoService) {}

	private _jobCnt = 1;
	private _getNextJobId(): string {
		const nextJob = `job_${this._jobCnt++}`;
		this.listenerManagers.set(nextJob, new ListenerManager<OnDataReadyListener>());
		console.log("Service Cache: created new job " + nextJob);
		return nextJob;
	}
	private _currJob: string | undefined = undefined;
	private _nextJob: string | undefined = undefined;

	private _fetchTimeout: ReturnType<typeof setTimeout>;
	private _fetchEntries: CacheKey[] = [];
	// private _fetchEntryKeys: string[] = [];

	private _fetchingEntries: CacheKey[] = [];
	private _fetchingEntryKeys = new Set<string>();

	private processSearchResponse(response: SearchResponse) {
		const geoResponse = response.geoResponse;
		if (geoResponse && geoResponse.allLocations) {
			geoResponse.allLocations.forEach((loc) => {
				const { level_0_keys, level_1_keys, level_2_keys } = loc.request;
				let key: CacheKey | undefined;
				if (level_0_keys) {
					key = [level_0_keys];
					if (level_1_keys) {
						key.push(level_1_keys);
						if (level_2_keys) {
							key.push(level_2_keys);
						}
					}
				}
				if (key && loc.status !== "PROCESSING") {
					const location = (loc.locations || [])[0];
					if (location) {
						const keyStr = CacheKey_toString(key);
						// console.log(keyStr + ":" + JSON.stringify(location.geometry).substring(0, 100));
						const validUntil = loc.validUntil || getDefaultValidUntil();
						const entry: CacheHitResponse = { resolved: true, validUntil, value: location };
						this._cache.set(keyStr, entry);
						if (isUseIDBCache()) {
							putData(entry, DB_NAME, keyStr, true);
						}
						// todo - write to the idb if set
					} else if (
						loc.status === "NO_MATCH" || //
						loc.status === "MULTIPLE_MATCHES" ||
						loc.status === "COMPLETED"
					) {
						const keyStr = CacheKey_toString(key);
						const validUntil = loc.validUntil || getDefaultValidUntil();
						const entry: CacheHitResponse = { resolved: true, validUntil, value: null, status: loc.status };
						this._cache.set(keyStr, entry);
						if (isUseIDBCache()) {
							putData(entry, DB_NAME, keyStr, true);
						}
					} else {
						throw Error("Unhandled status " + loc.status);
					}
				}
			});
		}
	}

	private async requestEntries(entries: CacheKey[]) {
		try {
			const request: SearchRequest = {
				geo_request: entries.map((e) => ({
					//
					level_0_keys: e[0],
					level_1_keys: e[1],
					level_2_keys: e[2],
					//
				})),
				source: "locations",
				level: "",
			};

			const task_create_resp = await this.geoService.search_task_create(request);
			const infos = [];
			const info = (i) => {
				console.info(i);
				infos.push(i);
			};

			info({ service: "search_task_create", request, response: task_create_resp });

			let search_response: SearchResponse;
			const jobId = this._currJob;
			let intCnt = 0;
			const listenerManager = this.listenerManagers.get(jobId);
			// let cnt = 0;
			do {
				const t1 = Date.now();
				search_response = await this.geoService.search_task(task_create_resp.requestId);

				if (this.hasErrors(search_response)) {
					// the error is return in status from a SWW
					throw Error("Error in at least one response");
				}

				const t2 = Date.now();
				const t_diff = t2 - t1;
				if (t_diff < 1000) {
					await new Promise((resolve) => setTimeout(resolve, 1000 - t_diff));
				}

				info({ service: "search_task", requestId: task_create_resp.requestId, response: search_response });
				if (search_response?.geoResponse?.allLocations.length > 0) {
					this.processSearchResponse(search_response);
					listenerManager.safeLoop((listener) => listener.onDataReady(jobId, intCnt++, search_response.status === "COMPLETED"));
				}
			} while (search_response.status !== "COMPLETED" /* && cnt++ < 20 */);
			const filterGeo = (obj) => {
				const copy = { ...obj };
				if (copy.geometry !== undefined) {
					copy.geometry = "size: " + JSON.stringify(copy.geometry).length;
				}
				Object.keys(copy).forEach((key) => {
					const val = copy[key];
					if (typeof val === "object") {
						copy[key] = filterGeo(copy[key]);
					}
				});
				return copy;
			};
			console.info(filterGeo({ infos }));
		} catch (err) {
			this.processErroneousResponse(err, entries);
		}
	}

	private hasErrors(search_response: SearchResponse): boolean {
		const geoResponse = search_response?.geoResponse;
		if (geoResponse?.allLocations.length > 0) {
			for (const item of search_response.geoResponse.allLocations) {
				if (item.status === "ERROR") {
					return true;
				}
			}
		}
		return false;
	}

	private processErroneousResponse(err: any, entries: CacheKey[]) {
		console.error(err);
		entries.forEach((entry) => {
			const keyStr = CacheKey_toString(entry);
			const cacheEntry: CacheHitResponse = { resolved: true, validUntil: getDefaultValidUntil(), value: undefined, status: ResponseStatus.ERROR };
			this._cache.set(keyStr, cacheEntry);
			if (isUseIDBCache()) {
				putData(entry, DB_NAME, keyStr, true);
			}
		});
		const jobId = this._currJob;
		let intCnt = 0;
		const listenerManager = this.listenerManagers.get(jobId);
		listenerManager.safeLoop((listener) => listener.onDataReady(jobId, intCnt++, true, true));
	}

	private scheduleDataFetch() {
		this._fetchTimeout = setTimeout(() => {
			this._nextJob = this._getNextJobId();
			const entriesToFetch = this._fetchEntries;
			this._fetchEntries = [];
			this.requestEntries(entriesToFetch) //
				.then((res) => {
					this._fetchTimeout = undefined; // clear it after full retrieval
					const jobId = this._currJob;
					this._currJob = this._nextJob;
					this._nextJob = undefined;
					if (this._fetchEntries.length > 0) {
						this.scheduleDataFetch();
					}
					const listenerManager = this.listenerManagers.get(jobId);

					listenerManager.clear();
					this.listenerManagers.delete(jobId);
				});
		}, FETCH_TIMEOUT);
	}

	getEntry(key: CacheKey): ServiceCacheEntry {
		if (!Array.isArray(key) || key.length < 1) {
			throw Error("Invalid key");
		}
		const keyStr = CacheKey_toString(key);
		if (this._cache.has(keyStr)) {
			const hit = this._cache.get(keyStr);
			return hit;
		}

		this._fetchEntries.push(key);
		// this._fetchEntryKeys.push(keyStr);

		if (!this._fetchTimeout) {
			// not scheduled, let's schedule one soon to be unleashed
			if (!this._currJob) {
				this._currJob = this._getNextJobId();
			}
			this.scheduleDataFetch();
			const entry = { jobId: this._currJob };
			this._cache.set(keyStr, entry);
			return entry;
		} else {
			// it's scheduled
			const jobId =
				this._nextJob || // and remote fetch started (as _currJob) so this entry will go with _nextJob
				this._currJob; // not yet started so this entry will go with _currJob
			const entry = { jobId: jobId };
			this._cache.set(keyStr, entry);
			return entry;
		}
	}

	addDataReadyListener(jobId: string, listener: OnDataReadyListener): void {
		const listenerManager = this.listenerManagers.get(jobId);
		listenerManager.addListener(listener);
	}

	private _isLoadingFromIdb = false;
	loadFromIdb(): Promise<void> {
		this._isLoadingFromIdb = true;
		return this.loadFromIdbAsync();
	}

	private async loadFromIdbAsync() {
		const keys = await getAllKeys(DB_NAME);
		const keysToDelete: string[] = [];
		for (const key of keys) {
			const data = await getData<string>(DB_NAME, key, true);
			const entryData = JSON.parse(data) as CacheHitResponse;
			if ((entryData.validUntil && Number.isInteger(entryData.validUntil) && Date.now() < entryData.validUntil) || entryData.status === "ERROR") {
				this._cache.set(key, entryData);
			} else {
				keysToDelete.push(key);
			}
		}
		if (keysToDelete.length > 0) {
			for (const key of keysToDelete) {
				console.warn("Deleting " + key);
				await deleteData(DB_NAME, key);
			}
		}
		this._isLoadingFromIdb = false;
	}

	public isLoadingFromIdb() {
		return this._isLoadingFromIdb;
	}
}
